// src/App.tsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DeviceProvider } from './context/DeviceContext';
import { Toaster } from 'react-hot-toast';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import AddDevice from './pages/AddDevice';
import EditDevice from './pages/EditDevice';
import Settings from './pages/Settings';
import NotificationSettings from './pages/NotificationSettings';
import InsuranceReport from './pages/InsuranceReport';
import AdminDashboard from './pages/AdminDashboard';
import Community from './pages/Community';
import Impressum from './pages/Impressum';

// Components
import Navigation from './components/Navigation';
import MobileNav from './components/MobileNav';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import { FirebaseStatus } from './components/FirebaseStatus';
import { PWAInstallPrompt } from './components/PWAInstallPrompt';
import { NotificationService } from './services/NotificationService';

// Styles
import './styles/App.css';
import './styles/voltfox-theme.css';
import './styles/MobileNav.css';

function App() {
  useEffect(() => {
    // Console Easter Egg
    console.log(
      '%c🦊 VoltFox is watching your batteries!',
      'color: #FF6B35; font-size: 20px; font-weight: bold;'
    );
    console.log(
      '%c💡 Created by Mr. Vision',
      'color: #FFD23F; font-size: 14px;'
    );
    console.log(
      '%cNever let them die again! Join us at voltfox.app',
      'color: #FF6B35; font-size: 12px;'
    );

    // Initialize FCM foreground listener
    NotificationService.initializeForegroundListener();

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }
  }, []);

  return (
    <Router>
      <AuthProvider>
        <DeviceProvider>
          <div className="voltfox-app">
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'linear-gradient(135deg, #FF6B35 0%, #FFD23F 100%)',
                  color: '#fff',
                  fontWeight: 600
                },
                success: {
                  iconTheme: {
                    primary: '#fff',
                    secondary: '#10B981'
                  }
                }
              }}
            />

            <Navigation />
            <MobileNav />

            <main className="voltfox-content">
              <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />

                  <Route path="/dashboard" element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } />

                  <Route path="/devices" element={
                    <ProtectedRoute>
                      <Devices />
                    </ProtectedRoute>
                  } />

                  <Route path="/add-device" element={
                    <ProtectedRoute>
                      <AddDevice />
                    </ProtectedRoute>
                  } />

                  <Route path="/edit-device/:id" element={
                    <ProtectedRoute>
                      <EditDevice />
                    </ProtectedRoute>
                  } />

                  <Route path="/settings" element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } />

                  <Route path="/notifications" element={
                    <ProtectedRoute>
                      <NotificationSettings />
                    </ProtectedRoute>
                  } />

                  <Route path="/insurance" element={
                    <ProtectedRoute>
                      <InsuranceReport />
                    </ProtectedRoute>
                  } />

                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                      <AdminDashboard />
                    </ProtectedRoute>
                  } />

                  <Route path="/community" element={
                    <ProtectedRoute>
                      <Community />
                    </ProtectedRoute>
                  } />

                  <Route path="/impressum" element={<Impressum />} />

                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </main>

            <Footer />

            {/* PWA Install Prompt */}
            <PWAInstallPrompt />
          </div>
        </DeviceProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
